import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';
import { ERROR, SUCCESS } from '~/types/toast.types';

const SessionTimeout = () => {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const { params } = useRouting();
  const { clientId } = params as { clientId: string };

  const SESSION_TIMEOUT_OPTIONS = [
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.twentyFourHoursTimeout',
        defaultMessage: '24 hours',
        description: 'Session timeout option for 24 hours',
      }),
      value: 86400,
    },
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.threeDaysTimeout',
        defaultMessage: '3 days',
        description: 'Session timeout option for 3 days',
      }),
      value: 259200,
    },
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.sevenDaysTimeout',
        defaultMessage: '7 days',
        description: 'Session timeout option for 7 days',
      }),
      value: 604800,
    },
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.customTimeout',
        defaultMessage: 'Custom',
        description: 'Custom session timeout option',
      }),
      value: 'custom',
    },
  ];

  const INACTIVE_TIMEOUT_OPTIONS = [
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.oneHourInactive',
        defaultMessage: '1 hour',
        description: 'Inactive timeout option for 1 hour',
      }),
      value: 3600,
    },
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.twoHoursInactive',
        defaultMessage: '2 hours',
        description: 'Inactive timeout option for 2 hours',
      }),
      value: 7200,
    },
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.eightHoursInactive',
        defaultMessage: '8 hours',
        description: 'Inactive timeout option for 8 hours',
      }),
      value: 28800,
    },
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.twelveHoursInactive',
        defaultMessage: '12 hours',
        description: 'Inactive timeout option for 12 hours',
      }),
      value: 43200,
    },
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.twentyFourHoursInactive',
        defaultMessage: '24 hours',
        description: 'Inactive timeout option for 24 hours',
      }),
      value: 86400,
    },
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.threeDaysInactive',
        defaultMessage: '3 days',
        description: 'Inactive timeout option for 3 days',
      }),
      value: 259200,
    },
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.sevenDaysInactive',
        defaultMessage: '7 days',
        description: 'Inactive timeout option for 7 days',
      }),
      value: 604800,
    },
    {
      label: intl.formatMessage({
        id: 'SessionTimeout.custom',
        defaultMessage: 'Custom',
        description: 'Custom inactive timeout option',
      }),
      value: 'custom',
    },
  ];

  const [customInactiveTimeoutBase, setCustomInactiveTimeoutBase] = useState(0);
  const [customInactiveTimeoutValue, setCustomInactiveTimeoutValue] = useState<
    number | null
  >(null);
  const [inactiveTimeoutInSeconds, setInactiveTimeoutInSeconds] = useState<
    number | string
  >(3600);
  const [inactiveTimeoutError, setInactiveTimeoutError] = useState('');

  const [customSessionTimeoutBase, setCustomSessionTimeoutBase] = useState(0);
  const [customSessionTimeoutValue, setCustomSessionTimeoutValue] = useState<
    number | null
  >(null);
  const [sessionTimeoutInSeconds, setSessionTimeoutInSeconds] = useState<
    number | string
  >(3600);
  const [sessionTimeoutError, setTiSessionTimeoutError] = useState('');

  const [updateClientAdmin, updateClientResult] =
    api.endpoints.updateClientAdmin.useMutation();
  const {
    error: updateClientErrorMsg,
    isError: isUpdateClientError,
    isSuccess: isUpdateClientSuccess,
    isLoading: isUpdateClientLoading,
  } = updateClientResult;

  useEffect(() => {
    if (isUpdateClientSuccess) {
      const message = intl.formatMessage({
        id: 'SessionTimeout.updateSuccess',
        defaultMessage:
          'Successfully updated client session timeout configuration.',
        description:
          'Success message when session timeout configuration is updated',
      });
      showToast(SUCCESS, message);
    }
    if (isUpdateClientError) {
      const message = intl.formatMessage(
        {
          id: 'SessionTimeout.updateError',
          defaultMessage:
            'Failed to update client session timeout configuration due to {errorMessage}.',
          description:
            'Error message when session timeout configuration update fails. Example: errorMessage = "network error"',
        },
        {
          errorMessage: `${updateClientErrorMsg}`,
        },
      );

      showToast(ERROR, message);
    }
  }, [isUpdateClientSuccess, isUpdateClientError, updateClientErrorMsg]);

  useEffect(() => {
    if (!currentUser) return;

    const userInactiveTimeoutInSeconds =
      currentUser?.client_config?.inactive_timeout_in_seconds;
    const userSessionTimeoutInSeconds =
      currentUser?.client_config?.session_timeout_in_seconds;

    if (!!userInactiveTimeoutInSeconds) {
      if (
        !INACTIVE_TIMEOUT_OPTIONS.map((o) => o.value).includes(
          userInactiveTimeoutInSeconds,
        )
      ) {
        // custom value occurred
        const base = !(userInactiveTimeoutInSeconds % 86400)
          ? 86400
          : !(userInactiveTimeoutInSeconds % 3600)
            ? 3600
            : 60;
        setCustomInactiveTimeoutBase(base);
        setCustomInactiveTimeoutValue(
          Math.ceil(userInactiveTimeoutInSeconds / base),
        );
        setInactiveTimeoutInSeconds('custom');
      } else {
        setInactiveTimeoutInSeconds(userInactiveTimeoutInSeconds);
      }
    }

    if (!!userSessionTimeoutInSeconds) {
      if (
        !SESSION_TIMEOUT_OPTIONS.map((o) => o.value).includes(
          userSessionTimeoutInSeconds,
        )
      ) {
        // custom value occurred
        const base = !(userSessionTimeoutInSeconds % 86400) ? 86400 : 3600;
        setCustomSessionTimeoutBase(base);
        setCustomSessionTimeoutValue(
          Math.ceil(userInactiveTimeoutInSeconds / base),
        );
        setSessionTimeoutInSeconds('custom');
      } else {
        setSessionTimeoutInSeconds(userSessionTimeoutInSeconds);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (sessionTimeoutInSeconds === 'custom') {
      setTiSessionTimeoutError('');
    } else {
      validateSessionTimeout();
    }
  }, [sessionTimeoutInSeconds]);

  useEffect(() => {
    if (sessionTimeoutInSeconds !== 'custom' || !customSessionTimeoutValue)
      return;
    validateSessionTimeout();
    validateInactiveTimeout();
  }, [
    sessionTimeoutInSeconds,
    customSessionTimeoutBase,
    customSessionTimeoutValue,
  ]);

  useEffect(() => {
    if (inactiveTimeoutInSeconds === 'custom') {
      setInactiveTimeoutError('');
    } else {
      validateInactiveTimeout();
    }
  }, [inactiveTimeoutInSeconds]);

  useEffect(() => {
    if (inactiveTimeoutInSeconds !== 'custom' || !customInactiveTimeoutValue)
      return;
    validateInactiveTimeout();
    validateSessionTimeout();
  }, [
    inactiveTimeoutInSeconds,
    customInactiveTimeoutBase,
    customInactiveTimeoutValue,
  ]);

  const validateInactiveTimeout = () => {
    const currentInactiveTimeoutValue =
      inactiveTimeoutInSeconds === 'custom'
        ? (customInactiveTimeoutValue ?? 0) * customInactiveTimeoutBase
        : (inactiveTimeoutInSeconds as number);

    if (
      currentInactiveTimeoutValue < 60 ||
      currentInactiveTimeoutValue > 604800
    ) {
      setInactiveTimeoutError(
        intl.formatMessage({
          id: 'SessionTimeout.inactiveTimeoutRangeError',
          defaultMessage:
            'Value is outside the acceptable range. The acceptable range is 1 minute to 7 days.',
          description:
            'Error message when inactive timeout value is outside acceptable range',
        }),
      );
      return false;
    }

    const currentSessionTimeoutValue =
      sessionTimeoutInSeconds === 'custom'
        ? (customSessionTimeoutValue ?? 0) * customSessionTimeoutBase
        : (sessionTimeoutInSeconds as number);

    if (
      customSessionTimeoutValue &&
      currentInactiveTimeoutValue > currentSessionTimeoutValue
    ) {
      setInactiveTimeoutError(
        intl.formatMessage({
          id: 'SessionTimeout.inactiveTimeoutGreaterError',
          defaultMessage:
            'Idle Session Timeout must be equal to or lower than Reauthentication Frequency.',
          description:
            'Error message when inactive timeout is greater than session timeout',
        }),
      );
      return false;
    }

    setInactiveTimeoutError('');
    return true;
  };

  const validateSessionTimeout = () => {
    const currentSessionTimeoutValue =
      sessionTimeoutInSeconds === 'custom'
        ? (customSessionTimeoutValue ?? 0) * customSessionTimeoutBase
        : (sessionTimeoutInSeconds as number);

    if (
      currentSessionTimeoutValue < 86400 ||
      currentSessionTimeoutValue > 604800
    ) {
      setTiSessionTimeoutError(
        intl.formatMessage({
          id: 'SessionTimeout.sessionTimeoutRangeError',
          defaultMessage:
            'Value is outside the acceptable range. The acceptable range is 24 hours to 7 days.',
          description:
            'Error message when session timeout value is outside acceptable range',
        }),
      );
      return false;
    }

    const currentInactiveTimeoutValue =
      inactiveTimeoutInSeconds === 'custom'
        ? (customInactiveTimeoutValue ?? 0) * customInactiveTimeoutBase
        : (inactiveTimeoutInSeconds as number);

    if (
      customInactiveTimeoutValue &&
      currentInactiveTimeoutValue > currentSessionTimeoutValue
    ) {
      setTiSessionTimeoutError(
        intl.formatMessage({
          id: 'SessionTimeout.sessionTimeoutSmallerError',
          defaultMessage:
            'Reauthentication Frequency must be equal to or larger than Idle Session Timeout.',
          description:
            'Error message when session timeout is smaller than inactive timeout',
        }),
      );
      return false;
    }

    setTiSessionTimeoutError('');
    return true;
  };

  const onSubmit = () => {
    if (!validateInactiveTimeout() || !validateSessionTimeout()) return;
    const currentInactiveTimeoutValue =
      inactiveTimeoutInSeconds === 'custom'
        ? (customInactiveTimeoutValue ?? 0) * customInactiveTimeoutBase
        : (inactiveTimeoutInSeconds as number);

    const currentSessionTimeoutValue =
      sessionTimeoutInSeconds === 'custom'
        ? (customSessionTimeoutValue ?? 0) * customSessionTimeoutBase
        : (sessionTimeoutInSeconds as number);

    updateClientAdmin({
      clientId,
      inactiveTimeoutInSeconds: currentInactiveTimeoutValue,
      sessionTimeoutInSeconds: currentSessionTimeoutValue,
    });
  };

  return (
    <Accordion
      items={[
        {
          title: intl.formatMessage({
            id: 'SessionTimeout.accordionTitle',
            defaultMessage: 'Session Timeout',
            description: 'Session timeout accordion title',
          }),
          isBordered: true,
          isExpanded: true,
          headerBorder: 'bottom',
          content: (
            <Layout preset="sections" p={6}>
              <Layout direction="column" spacing={2}>
                <Text variant="body-bold">
                  {intl.formatMessage({
                    id: 'SessionTimeout.idleSessionTimeoutTitle',
                    defaultMessage: 'Idle Session Timeout',
                    description: 'Idle session timeout section title',
                  })}
                </Text>
                <Layout
                  direction="row"
                  spacing={1}
                  justify="initial"
                  align="center"
                >
                  <Text>
                    {intl.formatMessage({
                      id: 'SessionTimeout.usersLoggedOutAfter',
                      defaultMessage:
                        'Users will be automatically logged out after',
                      description: 'Text describing automatic logout timing',
                    })}
                  </Text>
                  <Layout inline={true} spacing={2}>
                    <Select
                      isMulti={false}
                      isSearchable={false}
                      isClearable={false}
                      name="custom-inactive-timeout-select"
                      width="input.s.width"
                      error={inactiveTimeoutError}
                      value={inactiveTimeoutInSeconds}
                      options={INACTIVE_TIMEOUT_OPTIONS}
                      onChange={(value) => {
                        if (value === inactiveTimeoutInSeconds) return;
                        setInactiveTimeoutInSeconds(value as string | number);
                        setCustomInactiveTimeoutBase(
                          value === 'custom' ? 3600 : 0,
                        );
                        setCustomInactiveTimeoutValue(null);
                      }}
                    />
                    {customInactiveTimeoutBase > 0 && (
                      <>
                        <NumberInput
                          min={0}
                          autoFocus={!customInactiveTimeoutValue}
                          name="custom-inactive-timeout-value"
                          width="input.m.width"
                          error={inactiveTimeoutError}
                          value={customInactiveTimeoutValue}
                          onBlur={validateInactiveTimeout}
                          placeholder={intl.formatMessage({
                            id: 'SessionTimeout.inactiveTimeoutPlaceholder',
                            defaultMessage: 'Min 1 minute, Max 7 days',
                            description:
                              'Placeholder text for inactive timeout input',
                          })}
                          onChange={(value) => {
                            setCustomInactiveTimeoutValue(value);
                            validateInactiveTimeout();
                          }}
                        />
                        <Select
                          isMulti={false}
                          isSearchable={false}
                          isClearable={false}
                          name="custom-inactive-timeout-base-select"
                          width="input.s.width"
                          value={customInactiveTimeoutBase}
                          options={[
                            {
                              label: intl.formatMessage({
                                id: 'SessionTimeout.minute',
                                defaultMessage: 'minute',
                                description: 'Time unit: minute',
                              }),
                              value: 60,
                            },
                            {
                              label: intl.formatMessage({
                                id: 'SessionTimeout.hours',
                                defaultMessage: 'hours',
                                description: 'Time unit: hours',
                              }),
                              value: 3600,
                            },
                            {
                              label: intl.formatMessage({
                                id: 'SessionTimeout.days',
                                defaultMessage: 'days',
                                description: 'Time unit: days',
                              }),
                              value: 86400,
                            },
                          ]}
                          onChange={(value) => {
                            setCustomInactiveTimeoutBase(value as number);
                            validateInactiveTimeout();
                          }}
                        />
                      </>
                    )}
                  </Layout>
                  <Text>
                    {intl.formatMessage({
                      id: 'SessionTimeout.ofInactivity',
                      defaultMessage: 'of inactivity.',
                      description: 'Text describing period of inactivity',
                    })}
                  </Text>
                </Layout>
                {inactiveTimeoutError && (
                  <Text variant="tiny" color="status.danger">
                    {inactiveTimeoutError}
                  </Text>
                )}
              </Layout>
              <Layout direction="column" spacing={2}>
                <Text variant="body-bold">
                  {intl.formatMessage({
                    id: 'SessionTimeout.reauthenticationFrequencyTitle',
                    defaultMessage: 'Reauthentication Frequency',
                    description: 'Reauthentication frequency section title',
                  })}
                </Text>
                <Layout
                  direction="row"
                  spacing={1}
                  justify="initial"
                  align="center"
                >
                  <Text>
                    {intl.formatMessage({
                      id: 'SessionTimeout.usersRequiredLogin',
                      defaultMessage: 'Users will be required to login every',
                      description:
                        'Text describing login requirement frequency',
                    })}
                  </Text>
                  <Layout inline={true} spacing={2}>
                    <Select
                      isMulti={false}
                      isSearchable={false}
                      isClearable={false}
                      name="custom-session-timeout"
                      value={sessionTimeoutInSeconds}
                      width="input.s.width"
                      error={sessionTimeoutError}
                      options={SESSION_TIMEOUT_OPTIONS}
                      onChange={(value) => {
                        if (value === sessionTimeoutInSeconds) return;
                        setSessionTimeoutInSeconds(value as string | number);
                        setCustomSessionTimeoutBase(
                          value === 'custom' ? 3600 : 0,
                        );
                        setCustomSessionTimeoutValue(null);
                      }}
                    />
                    {customSessionTimeoutBase > 0 && (
                      <>
                        <NumberInput
                          min={0}
                          autoFocus={!customSessionTimeoutValue}
                          name="custom-session-timeout-value"
                          width="input.m.width"
                          error={sessionTimeoutError}
                          value={customSessionTimeoutValue}
                          onBlur={validateSessionTimeout}
                          placeholder={intl.formatMessage({
                            id: 'SessionTimeout.sessionTimeoutPlaceholder',
                            defaultMessage: 'Min 24 hours, Max 7 days',
                            description:
                              'Placeholder text for session timeout input',
                          })}
                          onChange={(value) => {
                            setCustomSessionTimeoutValue(value);
                            validateSessionTimeout();
                          }}
                        />
                        <Select
                          isMulti={false}
                          isSearchable={false}
                          isClearable={false}
                          name="custom-session-timeout-select-base"
                          width="input.s.width"
                          value={customSessionTimeoutBase}
                          options={[
                            {
                              label: intl.formatMessage({
                                id: 'SessionTimeout.hours',
                                defaultMessage: 'hours',
                                description: 'Time unit: hours',
                              }),
                              value: 3600,
                            },
                            {
                              label: intl.formatMessage({
                                id: 'SessionTimeout.days',
                                defaultMessage: 'days',
                                description: 'Time unit: days',
                              }),
                              value: 86400,
                            },
                          ]}
                          onChange={(value) => {
                            setCustomSessionTimeoutBase(value as number);
                            validateSessionTimeout();
                          }}
                        />
                      </>
                    )}
                  </Layout>
                  <Text>.</Text>
                </Layout>
                {sessionTimeoutError && (
                  <Text variant="tiny" color="status.danger">
                    {sessionTimeoutError}
                  </Text>
                )}
              </Layout>
              <Layout direction="row" justify="initial" align="center">
                <Button
                  isLoading={isUpdateClientLoading}
                  id="save-session-timeout-config"
                  disabled={!!sessionTimeoutError || !!inactiveTimeoutError}
                  onClick={onSubmit}
                  text={intl.formatMessage({
                    id: 'SessionTimeout.saveChangesButton',
                    defaultMessage: 'Save Changes',
                    description: 'Save changes button text',
                  })}
                  label="save-session-timeout"
                  tooltip={intl.formatMessage({
                    id: 'SessionTimeout.saveChangesTooltip',
                    defaultMessage: 'Save Changes',
                    description: 'Save changes button tooltip',
                  })}
                  variant="primary"
                />
              </Layout>
            </Layout>
          ),
        },
      ]}
    />
  );
};

export default SessionTimeout;
